import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ChevronLeft,
    ChevronRight,
    Settings,
    AlertTriangle,
    X,
    Users,
    MapPin,
    Search,
    Clock,
    Plus,
    GripVertical,
    Trash2,
    CalendarCheck,
    CheckCircle2,
    Droplets, // For liquid time
} from 'lucide-react';
import { useAppState, CalendarEvent, Task } from '../../contexts/AppContext';

export default function AppCalendar() {
    const { events, tasks, updateTask, updateEvent, softDeleteTask, softDeleteEvent, refreshGoogleEvents } = useAppState();
    const [viewMode, setViewMode] = useState<'day' | 'week' | 'month'>('day');
    const [selectedDate, setSelectedDate] = useState(new Date());

    // Banner dismissal persists for the current day using localStorage
    const [showBanner, setShowBanner] = useState(() => {
        const dismissed = localStorage.getItem('calendar_banner_dismissed');
        if (!dismissed) return true;
        try {
            const { date } = JSON.parse(dismissed);
            // Reset if it's a new day
            const today = new Date().toISOString().split('T')[0];
            return date !== today;
        } catch {
            return true;
        }
    });

    // Persist banner dismissal
    const dismissBanner = () => {
        const today = new Date().toISOString().split('T')[0];
        localStorage.setItem('calendar_banner_dismissed', JSON.stringify({ date: today }));
        setShowBanner(false);
    };

    // Unified selection state
    type SelectedItem =
        | { type: 'event'; data: CalendarEvent }
        | { type: 'task'; data: Task };

    const navigate = useNavigate();
    const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);

    const [showAddEvent, setShowAddEvent] = useState(false);
    const [showFindTime, setShowFindTime] = useState(false);
    const [draggedTask, setDraggedTask] = useState<Task | null>(null);
    const [draggedScheduledTask, setDraggedScheduledTask] = useState<Task | null>(null);
    const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null);
    const [dropTargetHour, setDropTargetHour] = useState<number | null>(null);
    const timelineRef = useRef<HTMLDivElement>(null);

    // Get current month info
    const currentMonth = selectedDate.getMonth();
    const currentYear = selectedDate.getFullYear();

    // 24-hour time slots
    const timeSlots = Array.from({ length: 24 }, (_, i) => {
        if (i === 0) return '12 AM';
        if (i < 12) return `${i} AM`;
        if (i === 12) return '12 PM';
        return `${i - 12} PM`;
    });

    // Generate calendar grid
    const calendarDays = useMemo(() => {
        const firstDay = new Date(currentYear, currentMonth, 1);
        const lastDay = new Date(currentYear, currentMonth + 1, 0);
        const startPadding = firstDay.getDay();
        const daysInMonth = lastDay.getDate();
        const prevMonthLastDay = new Date(currentYear, currentMonth, 0).getDate();

        const days: { date: Date; isCurrentMonth: boolean }[] = [];

        for (let i = startPadding - 1; i >= 0; i--) {
            days.push({
                date: new Date(currentYear, currentMonth - 1, prevMonthLastDay - i),
                isCurrentMonth: false,
            });
        }

        for (let i = 1; i <= daysInMonth; i++) {
            days.push({
                date: new Date(currentYear, currentMonth, i),
                isCurrentMonth: true,
            });
        }

        const remaining = 42 - days.length;
        for (let i = 1; i <= remaining; i++) {
            days.push({
                date: new Date(currentYear, currentMonth + 1, i),
                isCurrentMonth: false,
            });
        }

        return days;
    }, [currentMonth, currentYear]);

    // Get tasks for a specific date
    const getTasksForDate = (date: Date) => {
        const dateStr = date.toISOString().split('T')[0];
        return tasks.filter((t) => t.dueDate === dateStr);
    };

    // Get unscheduled tasks for selected date (no time set)
    const getUnscheduledTasks = (date: Date) => {
        return getTasksForDate(date).filter((t) => !t.timeStart);
    };

    // Get scheduled tasks for selected date (has time)
    const getScheduledTasks = (date: Date) => {
        return getTasksForDate(date).filter((t) => t.timeStart);
    };

    // Generate recurring event instances for a given date range
    const generateRecurringInstances = (event: CalendarEvent, targetDate: Date): CalendarEvent | null => {
        if (!event.recurrence || event.recurrence.pattern === 'none') return null;
        if (!event.date) return null;

        const eventDate = new Date(event.date);
        const targetDateStr = targetDate.toISOString().split('T')[0];

        // Don't generate for dates before the original event
        if (targetDate < eventDate) return null;

        // Check if past end date
        if (event.recurrence.endDate && targetDateStr > event.recurrence.endDate) return null;

        const daysDiff = Math.floor((targetDate.getTime() - eventDate.getTime()) / (1000 * 60 * 60 * 24));
        const interval = event.recurrence.interval || 1;
        const targetDayOfWeek = targetDate.getDay();

        let shouldShow = false;

        switch (event.recurrence.pattern) {
            case 'daily':
                shouldShow = daysDiff % interval === 0;
                break;
            case 'weekdays':
                shouldShow = targetDayOfWeek >= 1 && targetDayOfWeek <= 5;
                break;
            case 'weekly':
                shouldShow = daysDiff % (7 * interval) === 0;
                break;
            case 'monthly':
                const eventDayOfMonth = eventDate.getDate();
                const targetDayOfMonth = targetDate.getDate();
                const monthsDiff = (targetDate.getFullYear() - eventDate.getFullYear()) * 12 +
                    (targetDate.getMonth() - eventDate.getMonth());
                shouldShow = targetDayOfMonth === eventDayOfMonth && monthsDiff % interval === 0;
                break;
        }

        if (shouldShow && targetDateStr !== event.date) {
            return {
                ...event,
                id: `${event.id}_${targetDateStr}`,
                date: targetDateStr,
                recurrence: {
                    ...event.recurrence,
                    parentEventId: event.id,
                },
            };
        }

        return null;
    };

    // Filter events for the selected date (including recurring instances)
    const selectedDateEvents = useMemo(() => {
        const selectedDateStr = selectedDate.toISOString().split('T')[0];
        const result: CalendarEvent[] = [];

        events.forEach(e => {
            // Regular event for this date
            if (e.date === selectedDateStr) {
                result.push(e);
            } else if (e.recurrence && e.recurrence.pattern !== 'none') {
                // Check for recurring instance
                const instance = generateRecurringInstances(e, selectedDate);
                if (instance) {
                    result.push(instance);
                }
            }
        });

        return result;
    }, [events, selectedDate]);

    // Sync with Google Calendar when date filters change
    useEffect(() => {
        let start: Date, end: Date;

        if (viewMode === 'day') {
            start = new Date(selectedDate);
            start.setHours(0, 0, 0, 0);
            end = new Date(selectedDate);
            end.setHours(23, 59, 59, 999);
        } else if (viewMode === 'week') {
            const sunday = new Date(selectedDate);
            sunday.setDate(selectedDate.getDate() - selectedDate.getDay());
            sunday.setHours(0, 0, 0, 0);
            start = sunday;

            const saturday = new Date(sunday);
            saturday.setDate(sunday.getDate() + 6);
            saturday.setHours(23, 59, 59, 999);
            end = saturday;
        } else { // month
            start = new Date(currentYear, currentMonth, 1);
            start.setHours(0, 0, 0, 0);
            end = new Date(currentYear, currentMonth + 1, 0);
            end.setHours(23, 59, 59, 999);
        }

        refreshGoogleEvents(start, end);
    }, [selectedDate, viewMode, refreshGoogleEvents, currentMonth, currentYear]);

    const unscheduledTasks = getUnscheduledTasks(selectedDate);
    const scheduledTasks = getScheduledTasks(selectedDate);

    // Parse time string to hour (0-23) - defined early for conflict detection
    const parseTimeToHour = (time: string): number => {
        if (!time) return 0;
        const match = time.match(/(\d+):(\d+)\s*(AM|PM)/i);
        if (!match) return 0;
        let hour = parseInt(match[1]);
        const ampm = match[3].toUpperCase();
        if (ampm === 'PM' && hour !== 12) hour += 12;
        if (ampm === 'AM' && hour === 12) hour = 0;
        return hour;
    };

    // Intelligent Schedule Conflict Detection with Auto-Adjust Support
    interface ScheduleConflict {
        type: 'overlap' | 'overload' | 'late_meeting';
        message: string;
        suggestedAction: string;
        affectedItems: string[];
        // Enhanced fields for auto-adjust
        conflictingItemId?: string;
        conflictingItemType?: 'event' | 'task';
        suggestedNewTime?: { start: string; end: string; label: string };
    }

    // Convert hour to 12-hour format string
    const hourTo12Format = (hour: number) => {
        if (hour === 0) return '12:00 AM';
        if (hour < 12) return `${hour}:00 AM`;
        if (hour === 12) return '12:00 PM';
        return `${hour - 12}:00 PM`;
    };

    // Find the next available time slot for the given duration
    const findNextAvailableSlot = (
        durationHours: number,
        excludeItemId?: string
    ): { start: string; end: string; label: string } | null => {
        // Collect all occupied time slots (excluding the item we're moving)
        const occupiedSlots: { start: number; end: number }[] = [];

        selectedDateEvents.forEach((event) => {
            if (event.id !== excludeItemId) {
                const startHour = parseTimeToHour(event.timeStart);
                const endHour = parseTimeToHour(event.timeEnd);
                if (endHour > startHour) {
                    occupiedSlots.push({ start: startHour, end: endHour });
                }
            }
        });

        scheduledTasks.forEach((task) => {
            if (task.id !== excludeItemId && task.timeStart && task.timeEnd) {
                const startHour = parseTimeToHour(task.timeStart);
                const endHour = parseTimeToHour(task.timeEnd);
                if (endHour > startHour) {
                    occupiedSlots.push({ start: startHour, end: endHour });
                }
            }
        });

        // Sort by start time
        occupiedSlots.sort((a, b) => a.start - b.start);

        // Working hours: 8 AM to 6 PM
        const workStart = 8;
        const workEnd = 18;

        // Current hour (if today, don't suggest past times)
        const now = new Date();
        const isToday = selectedDate.toDateString() === now.toDateString();
        const currentHour = isToday ? Math.max(now.getHours() + 1, workStart) : workStart;

        // Find gaps in the schedule
        let searchStart = currentHour;

        for (const slot of occupiedSlots) {
            // Check if there's a gap before this occupied slot
            if (slot.start > searchStart) {
                const gapSize = slot.start - searchStart;
                if (gapSize >= durationHours) {
                    return {
                        start: hourTo12Format(searchStart),
                        end: hourTo12Format(searchStart + durationHours),
                        label: `${hourTo12Format(searchStart)} - ${hourTo12Format(searchStart + durationHours)}`
                    };
                }
            }
            // Move search start past this occupied slot
            searchStart = Math.max(searchStart, slot.end);
        }

        // Check if there's room after all occupied slots
        if (searchStart + durationHours <= workEnd) {
            return {
                start: hourTo12Format(searchStart),
                end: hourTo12Format(searchStart + durationHours),
                label: `${hourTo12Format(searchStart)} - ${hourTo12Format(searchStart + durationHours)}`
            };
        }

        // Suggest tomorrow if no slot found today
        return {
            start: hourTo12Format(workStart),
            end: hourTo12Format(workStart + durationHours),
            label: `Tomorrow ${hourTo12Format(workStart)} - ${hourTo12Format(workStart + durationHours)}`
        };
    };

    const detectScheduleConflicts = useMemo((): ScheduleConflict | null => {
        const allScheduledItems: { id: string; type: 'event' | 'task'; title: string; start: number; end: number; isRecurring: boolean }[] = [];

        // Collect all scheduled events
        selectedDateEvents.forEach((event) => {
            const startHour = parseTimeToHour(event.timeStart);
            const endHour = parseTimeToHour(event.timeEnd);
            const isRecurring = !!(event.recurrence && event.recurrence.pattern && event.recurrence.pattern !== 'none');
            allScheduledItems.push({ id: event.id, type: 'event', title: event.title, start: startHour, end: endHour, isRecurring });
        });

        // Collect all scheduled tasks (tasks are not recurring in this context)
        scheduledTasks.forEach((task) => {
            if (task.timeStart && task.timeEnd) {
                const startHour = parseTimeToHour(task.timeStart);
                const endHour = parseTimeToHour(task.timeEnd);
                allScheduledItems.push({ id: task.id, type: 'task', title: task.title, start: startHour, end: endHour, isRecurring: false });
            }
        });

        // Check for overlaps
        for (let i = 0; i < allScheduledItems.length; i++) {
            for (let j = i + 1; j < allScheduledItems.length; j++) {
                const a = allScheduledItems[i];
                const b = allScheduledItems[j];
                if (a.start < b.end && a.end > b.start) {
                    // Find the item that was added later (likely the conflict causer)
                    const itemToMove = j > i ? b : a;
                    const duration = itemToMove.end - itemToMove.start || 1;
                    const suggested = findNextAvailableSlot(duration, itemToMove.id);

                    return {
                        type: 'overlap',
                        message: `"${a.title}" overlaps with "${b.title}"`,
                        suggestedAction: suggested ? `Move "${itemToMove.title}" to ${suggested.label}` : 'Reschedule one of these items',
                        affectedItems: [a.title, b.title],
                        conflictingItemId: itemToMove.id,
                        conflictingItemType: itemToMove.type,
                        suggestedNewTime: suggested || undefined,
                    };
                }
            }
        }

        // Check for overload (more than 8 hours scheduled)
        const totalHours = allScheduledItems.reduce((sum, item) => sum + (item.end - item.start), 0);
        if (totalHours > 8) {
            const lastItem = allScheduledItems[allScheduledItems.length - 1];
            const duration = lastItem ? (lastItem.end - lastItem.start || 1) : 1;

            return {
                type: 'overload',
                message: `You have ${totalHours} hours scheduled today`,
                suggestedAction: `Consider moving "${lastItem?.title || 'a task'}" to tomorrow`,
                affectedItems: [lastItem?.title || ''],
                conflictingItemId: lastItem?.id,
                conflictingItemType: lastItem?.type,
                suggestedNewTime: {
                    start: '8:00 AM',
                    end: hourTo12Format(8 + duration),
                    label: `Tomorrow 8:00 AM - ${hourTo12Format(8 + duration)}`
                },
            };
        }

        // Check for late evening meetings (after 6 PM) - but EXCLUDE recurring events
        // Recurring events are intentionally scheduled, so don't warn about them
        const lateItems = allScheduledItems.filter((item) => item.end > 18 && !item.isRecurring);
        if (lateItems.length > 0) {
            const lateItem = lateItems[0];
            const duration = lateItem.end - lateItem.start || 1;
            const suggested = findNextAvailableSlot(duration, lateItem.id);

            return {
                type: 'late_meeting',
                message: `"${lateItem.title}" ends after 6 PM`,
                suggestedAction: suggested ? `Move to ${suggested.label}` : 'Reschedule to earlier in the day?',
                affectedItems: [lateItem.title],
                conflictingItemId: lateItem.id,
                conflictingItemType: lateItem.type,
                suggestedNewTime: suggested || undefined,
            };
        }

        return null;
    }, [selectedDateEvents, scheduledTasks, selectedDate]);

    // Show banner based on conflict detection
    const shouldShowNegotiationBanner = showBanner && detectScheduleConflicts !== null;

    //     refreshGoogleEvents(start, end);
    // }, [currentMonth, currentYear, refreshGoogleEvents]);

    // But we are in 'day' view mostly. Fetching whole month is safer.

    const formatDate = (date: Date) => {
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
        });
    };

    const formatMonthYear = (date: Date) => {
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };

    const isSameDay = (d1: Date, d2: Date) => {
        return (
            d1.getDate() === d2.getDate() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getFullYear() === d2.getFullYear()
        );
    };

    const isToday = (date: Date) => {
        return isSameDay(date, new Date());
    };

    const goToPrevMonth = () => {
        setSelectedDate(new Date(currentYear, currentMonth - 1, 1));
    };

    const goToNextMonth = () => {
        setSelectedDate(new Date(currentYear, currentMonth + 1, 1));
    };

    const goToToday = () => {
        setSelectedDate(new Date());
    };

    // Navigation for week/day
    const goToPrevDay = () => {
        const newDate = new Date(selectedDate);
        newDate.setDate(newDate.getDate() - 1);
        setSelectedDate(newDate);
    };

    const goToNextDay = () => {
        const newDate = new Date(selectedDate);
        newDate.setDate(newDate.getDate() + 1);
        setSelectedDate(newDate);
    };

    const goToPrevWeek = () => {
        const newDate = new Date(selectedDate);
        newDate.setDate(newDate.getDate() - 7);
        setSelectedDate(newDate);
    };

    const goToNextWeek = () => {
        const newDate = new Date(selectedDate);
        newDate.setDate(newDate.getDate() + 7);
        setSelectedDate(newDate);
    };

    // Get week days for week view
    const getWeekDays = useMemo(() => {
        const sunday = new Date(selectedDate);
        sunday.setDate(selectedDate.getDate() - selectedDate.getDay());

        return Array.from({ length: 7 }, (_, i) => {
            const date = new Date(sunday);
            date.setDate(sunday.getDate() + i);
            return date;
        });
    }, [selectedDate]);

    // Format short day name
    const formatShortDay = (date: Date) => {
        return date.toLocaleDateString('en-US', { weekday: 'short' });
    };

    const formatDayNumber = (date: Date) => {
        return date.getDate();
    };

    const handleAutoAdjust = () => {
        if (!detectScheduleConflicts || !detectScheduleConflicts.suggestedNewTime) {
            dismissBanner();
            return;
        }

        const { conflictingItemId, conflictingItemType, suggestedNewTime } = detectScheduleConflicts;

        if (!conflictingItemId || !conflictingItemType) {
            dismissBanner();
            return;
        }

        // Check if suggestion is for tomorrow
        const isTomorrow = suggestedNewTime.label.includes('Tomorrow');
        let newDate = selectedDate.toISOString().split('T')[0];

        if (isTomorrow) {
            const tomorrow = new Date(selectedDate);
            tomorrow.setDate(tomorrow.getDate() + 1);
            newDate = tomorrow.toISOString().split('T')[0];
        }

        // Update the conflicting item
        if (conflictingItemType === 'event') {
            updateEvent(conflictingItemId, {
                timeStart: suggestedNewTime.start,
                timeEnd: suggestedNewTime.end,
                date: newDate,
            });
        } else {
            updateTask(conflictingItemId, {
                timeStart: suggestedNewTime.start,
                timeEnd: suggestedNewTime.end,
                dueDate: newDate,
            });
        }

        dismissBanner();
    };

    const handleEventClick = (event: CalendarEvent) => {
        // Navigate to event detail page for full editing
        navigate(`/app/event/${event.id}`);
    };

    const handleTaskClick = (task: Task) => {
        // Navigate to task detail page for full editing
        navigate(`/app/task/${task.id}`);
    };

    const closeItemDetail = () => {
        setSelectedItem(null);
    };

    // Note: hourTo12Format is defined earlier in the file at line ~259

    // Drag handlers for unscheduled tasks
    const handleDragStart = (task: Task) => {
        setDraggedTask(task);
    };

    // Drag handlers for scheduled tasks (rescheduling)
    const handleScheduledDragStart = (task: Task) => {
        setDraggedScheduledTask(task);
    };

    // Drag handlers for events (rescheduling)
    const handleEventDragStart = (event: CalendarEvent) => {
        setDraggedEvent(event);
    };

    const handleDragEnd = () => {
        setDraggedTask(null);
        setDraggedScheduledTask(null);
        setDraggedEvent(null);
        setDropTargetHour(null);
    };

    const handleDragOver = (e: React.DragEvent, hour: number) => {
        e.preventDefault();
        setDropTargetHour(hour);
    };

    const handleDragLeave = () => {
        setDropTargetHour(null);
    };

    const handleDrop = (e: React.DragEvent, hour: number) => {
        e.preventDefault();
        const startTime = hourTo12Format(hour);
        const endTime = hourTo12Format(hour + 1);
        const dateStr = selectedDate.toISOString().split('T')[0];

        // Handle unscheduled task drop
        if (draggedTask) {
            updateTask(draggedTask.id, {
                timeStart: startTime,
                timeEnd: endTime,
                dueDate: dateStr,
            });
        }

        // Handle scheduled task reschedule
        if (draggedScheduledTask) {
            updateTask(draggedScheduledTask.id, {
                timeStart: startTime,
                timeEnd: endTime,
            });
        }

        // Handle event reschedule
        if (draggedEvent) {
            updateEvent(draggedEvent.id, {
                timeStart: startTime,
                timeEnd: endTime,
            });
        }

        setDraggedTask(null);
        setDraggedScheduledTask(null);
        setDraggedEvent(null);
        setDropTargetHour(null);
    };

    // Delete handlers
    const handleDeleteTask = (taskId: string) => {
        softDeleteTask(taskId);
    };

    const handleDeleteEvent = (eventId: string) => {
        softDeleteEvent(eventId);
    };

    // Get tasks scheduled for a specific hour
    const getTasksForHour = (hour: number) => {
        return scheduledTasks.filter((task) => {
            if (!task.timeStart) return false;
            const taskHour = parseTimeToHour(task.timeStart);
            return taskHour === hour;
        });
    };

    return (
        <div className="leading-[1.5] dark:bg-dark-bg min-h-screen">
            {/* Custom Header for Calendar */}
            <div className="px-4 pb-2">
                <div className="flex justify-between items-center">
                    <h1 className="text-xl font-semibold text-[#1A2832] dark:text-white">{formatMonthYear(selectedDate)}</h1>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={goToToday}
                            className="px-3 py-1 rounded-lg text-xs font-semibold text-[#0D5F6B] dark:text-dark-teal-light bg-[#D9EEF2] dark:bg-dark-elevated"
                        >
                            Today
                        </button>
                        <div className="flex p-1 rounded-lg bg-[#F5F8FA] dark:bg-dark-elevated">
                            {(['day', 'week', 'month'] as const).map((mode) => (
                                <button
                                    key={mode}
                                    onClick={() => setViewMode(mode)}
                                    className={`px-3 py-1 rounded text-xs font-semibold capitalize transition ${viewMode === mode
                                        ? 'bg-white dark:bg-dark-card shadow-[0_2px_8px_rgba(13,95,107,0.06),0_1px_2px_rgba(13,95,107,0.04)] dark:shadow-dark text-[#0D5F6B] dark:text-dark-teal-light'
                                        : 'text-[#7B8C9B] dark:text-dark-text-muted'
                                        }`}
                                >
                                    {mode}
                                </button>
                            ))}
                        </div>
                        <button className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F5F8FA] dark:bg-dark-elevated active:bg-[#EEF3F6] dark:active:bg-dark-border">
                            <Settings className="w-4 h-4 text-[#7B8C9B]" />
                        </button>
                    </div>
                </div>
            </div>

            {/* AI Schedule Negotiation Banner */}
            {shouldShowNegotiationBanner && detectScheduleConflicts && (
                <div className="px-4 pb-4">
                    <div className={`p-4 rounded-xl shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)] ${detectScheduleConflicts.type === 'overlap' ? 'bg-red-500' :
                        detectScheduleConflicts.type === 'overload' ? 'bg-[#E8A547]' :
                            'bg-blue-500'
                        }`}>
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-[18px] h-[18px] text-white flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-white mb-1">
                                    {detectScheduleConflicts.type === 'overlap' ? '⚠️ Schedule Conflict' :
                                        detectScheduleConflicts.type === 'overload' ? '📊 Heavy Day Ahead' :
                                            '🌙 Late Schedule'}
                                </p>
                                <p className="text-sm text-white/90">
                                    {detectScheduleConflicts.message}
                                </p>
                                <p className="text-xs text-white/70 mt-1">
                                    💡 {detectScheduleConflicts.suggestedAction}
                                </p>
                                <div className="flex gap-2 mt-3">
                                    {detectScheduleConflicts.suggestedNewTime ? (
                                        <button
                                            onClick={handleAutoAdjust}
                                            className="px-4 py-2 rounded-lg bg-white text-[#1A2832] text-sm font-semibold hover:bg-gray-100 transition flex items-center gap-2"
                                        >
                                            <CalendarCheck className="w-4 h-4" />
                                            Move to {detectScheduleConflicts.suggestedNewTime.label}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={dismissBanner}
                                            className="px-4 py-2 rounded-lg bg-white text-[#1A2832] text-sm font-semibold hover:bg-gray-100 transition"
                                        >
                                            Manual Adjust
                                        </button>
                                    )}
                                    <button
                                        onClick={dismissBanner}
                                        className="px-4 py-2 rounded-lg border border-white/50 text-white text-sm font-semibold hover:bg-white/10 transition"
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            </div>
                            <button onClick={dismissBanner} className="hover:bg-white/10 rounded p-1 transition">
                                <X className="w-4 h-4 text-white" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View-Specific Content */}

            {/* DAY VIEW */}
            {viewMode === 'day' && (
                <section className="mb-4 px-4">
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-2">
                            <button onClick={goToPrevDay} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F8FA]">
                                <ChevronLeft className="w-5 h-5 text-[#7B8C9B]" />
                            </button>
                            <h2 className="text-xl font-semibold text-[#1A2832]">{formatDate(selectedDate)}</h2>
                            <button onClick={goToNextDay} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F8FA]">
                                <ChevronRight className="w-5 h-5 text-[#7B8C9B]" />
                            </button>
                        </div>
                        <button
                            onClick={() => setShowAddEvent(true)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#0D5F6B] text-white text-sm font-semibold"
                        >
                            <Plus className="w-4 h-4" />
                            Add
                        </button>
                    </div>

                    {/* Unscheduled Tasks - Draggable */}
                    {unscheduledTasks.length > 0 && (
                        <div className="mb-4">
                            <h3 className="text-sm font-semibold text-[#7B8C9B] mb-2">
                                Drag tasks to schedule
                            </h3>
                            <div className="space-y-2">
                                {unscheduledTasks.map((task) => (
                                    <div
                                        key={task.id}
                                        draggable
                                        onDragStart={() => handleDragStart(task)}
                                        onDragEnd={handleDragEnd}
                                        className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-[0_2px_8px_rgba(13,95,107,0.06)] cursor-grab active:cursor-grabbing hover:shadow-md transition"
                                    >
                                        <GripVertical className="w-4 h-4 text-[#7B8C9B]" />
                                        <div
                                            className={`w-3 h-3 rounded-full ${task.completed ? 'bg-[#3DA58A]' : 'bg-[#DCE4EA]'
                                                }`}
                                        />
                                        <div className="flex-1">
                                            <p
                                                className={`text-sm font-medium ${task.completed ? 'text-[#7B8C9B] line-through' : 'text-[#1A2832]'
                                                    }`}
                                            >
                                                {task.title}
                                            </p>
                                        </div>
                                        <div
                                            className="px-2 py-1 rounded text-xs font-semibold"
                                            style={{ backgroundColor: task.contextBgColor, color: task.contextColor }}
                                        >
                                            {task.context}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 24-Hour Timeline */}
                    <div
                        ref={timelineRef}
                        className="bg-white rounded-2xl shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)] overflow-hidden"
                    >
                        <div className="max-h-[400px] overflow-y-auto">
                            {timeSlots.map((time, hour) => {
                                const hourTasks = getTasksForHour(hour);
                                const hourEvents = selectedDateEvents.filter((e) => {
                                    const eventHour = parseTimeToHour(e.timeStart);
                                    return eventHour === hour;
                                });
                                const isDropTarget = dropTargetHour === hour;

                                return (
                                    <div
                                        key={hour}
                                        className={`flex border-b border-[#EEF3F6] last:border-b-0 min-h-[60px] transition ${isDropTarget ? 'bg-[#D9EEF2]' : ''
                                            }`}
                                        onDragOver={(e) => handleDragOver(e, hour)}
                                        onDragLeave={handleDragLeave}
                                        onDrop={(e) => handleDrop(e, hour)}
                                    >
                                        {/* Time Label */}
                                        <div className="w-16 py-2 flex-shrink-0 border-r border-[#EEF3F6]">
                                            <span className="text-xs text-[#7B8C9B] block text-center">
                                                {time}
                                            </span>
                                        </div>

                                        {/* Events/Tasks for this hour */}
                                        <div className="flex-1 p-1 min-h-[60px] flex flex-col gap-1 relative group">
                                            {hourTasks.map((task) => (
                                                <div
                                                    key={task.id}
                                                    draggable
                                                    onDragStart={() => handleScheduledDragStart(task)}
                                                    onDragEnd={handleDragEnd}
                                                    className="rounded-lg p-2 mb-1 text-sm cursor-grab active:cursor-grabbing group relative"
                                                    style={{
                                                        backgroundColor: task.contextBgColor,
                                                        borderLeft: `3px solid ${task.contextColor}`,
                                                    }}
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div
                                                            className="flex-1 cursor-pointer"
                                                            onClick={() => handleTaskClick(task)}
                                                        >
                                                            <p className="font-medium text-[#1A2832]">{task.title}</p>
                                                            <p className="text-xs text-[#7B8C9B]">
                                                                {task.timeStart} - {task.timeEnd}
                                                            </p>
                                                        </div>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteTask(task.id);
                                                            }}
                                                            className="w-6 h-6 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 hover:bg-red-100 transition"
                                                        >
                                                            <Trash2 className="w-3 h-3 text-red-500" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                            {hourEvents.map((event) => (
                                                <div
                                                    key={event.id}
                                                    draggable
                                                    onDragStart={() => handleEventDragStart(event)}
                                                    onDragEnd={handleDragEnd}
                                                    className="w-full rounded-lg p-2 mb-1 text-left text-white cursor-grab active:cursor-grabbing group relative"
                                                    style={{ backgroundColor: event.color }}
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <button
                                                            onClick={() => handleEventClick(event)}
                                                            className="flex-1 text-left"
                                                        >
                                                            <p className="font-medium text-sm">{event.title}</p>
                                                            <p className="text-xs opacity-80">
                                                                {event.timeStart} - {event.timeEnd}
                                                            </p>
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteEvent(event.id);
                                                            }}
                                                            className="w-6 h-6 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 hover:bg-white/20 transition"
                                                        >
                                                            <Trash2 className="w-3 h-3 text-white" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                            {isDropTarget && (draggedTask || draggedScheduledTask || draggedEvent) && (
                                                <div className="rounded-lg p-2 bg-[#0D5F6B]/20 border-2 border-dashed border-[#0D5F6B] text-sm">
                                                    <p className="font-medium text-[#0D5F6B]">
                                                        Drop "{draggedTask?.title || draggedScheduledTask?.title || draggedEvent?.title}" here
                                                    </p>
                                                    <p className="text-xs text-[#0D5F6B]/70">
                                                        {hourTo12Format(hour)} - {hourTo12Format(hour + 1)}
                                                    </p>
                                                </div>
                                            )}
                                            {/* Liquid Time Indicator */}
                                            {(() => {
                                                // Calculate occupied minutes
                                                let occupiedMinutes = 0;

                                                // 1. Calculate from Tasks
                                                hourTasks.forEach(t => {
                                                    if (t.timeStart && t.timeEnd) {
                                                        const start = parseTimeToHour(t.timeStart) * 60 + parseInt(t.timeStart.split(':')[1]);
                                                        const end = parseTimeToHour(t.timeEnd) * 60 + parseInt(t.timeEnd.split(':')[1]);
                                                        // Overlap with current hour
                                                        const hourStart = hour * 60;
                                                        const hourEnd = (hour + 1) * 60;
                                                        const overlapStart = Math.max(start, hourStart);
                                                        const overlapEnd = Math.min(end, hourEnd);
                                                        if (overlapEnd > overlapStart) {
                                                            occupiedMinutes += (overlapEnd - overlapStart);
                                                        }
                                                    } else {
                                                        occupiedMinutes += 30; // Default for unscheduled tasks in this slot
                                                    }
                                                });

                                                // 2. Calculate from Events
                                                hourEvents.forEach(e => {
                                                    const start = parseTimeToHour(e.timeStart) * 60 + parseInt(e.timeStart.split(':')[1]);
                                                    const end = parseTimeToHour(e.timeEnd) * 60 + parseInt(e.timeEnd.split(':')[1]);
                                                    const hourStart = hour * 60;
                                                    const hourEnd = (hour + 1) * 60;
                                                    const overlapStart = Math.max(start, hourStart);
                                                    const overlapEnd = Math.min(end, hourEnd);
                                                    if (overlapEnd > overlapStart) {
                                                        occupiedMinutes += (overlapEnd - overlapStart);
                                                    }
                                                });

                                                const availableMinutes = Math.max(0, 60 - occupiedMinutes);

                                                // Only show if >= 15 mins available and generally during day (7am - 8pm)
                                                if (availableMinutes >= 15 && hour >= 7 && hour <= 20) {
                                                    const isFullHour = availableMinutes === 60;

                                                    // Don't render if it's the drop target (to avoid clutter)
                                                    if (isDropTarget) return null;

                                                    return (
                                                        <div className={`
                                                            w-full rounded-lg border-2 border-dotted border-[#DCE4EA] 
                                                            flex items-center justify-center gap-2 
                                                            text-xs font-medium text-[#7B8C9B] 
                                                            hover:bg-[#F5F8FA] hover:border-[#0D5F6B]/30 hover:text-[#0D5F6B] 
                                                            transition-colors cursor-pointer group/liquid
                                                            ${isFullHour ? 'py-4 mt-auto' : 'py-1 mt-auto'}
                                                        `}>
                                                            <Droplets className="w-3 h-3 opacity-50 group-hover/liquid:text-[#0D5F6B]" />
                                                            <span>Available: {availableMinutes} min</span>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* WEEK VIEW */}
            {viewMode === 'week' && (
                <section className="mb-4 px-4">
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-2">
                            <button onClick={goToPrevWeek} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F8FA]">
                                <ChevronLeft className="w-5 h-5 text-[#7B8C9B]" />
                            </button>
                            <h2 className="text-lg font-semibold text-[#1A2832]">
                                {getWeekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {getWeekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </h2>
                            <button onClick={goToNextWeek} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F8FA]">
                                <ChevronRight className="w-5 h-5 text-[#7B8C9B]" />
                            </button>
                        </div>
                        <button
                            onClick={() => setShowAddEvent(true)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#0D5F6B] text-white text-sm font-semibold"
                        >
                            <Plus className="w-4 h-4" />
                            Add
                        </button>
                    </div>

                    {/* Week Day Headers */}
                    <div className="bg-white dark:bg-dark-card rounded-2xl shadow-[0_4px_16px_rgba(13,95,107,0.08)] dark:shadow-dark overflow-hidden">
                        <div className="grid grid-cols-7 border-b border-[#EEF3F6] dark:border-dark-border">
                            {getWeekDays.map((date, i) => {
                                const dayTasks = getTasksForDate(date);
                                const isSelectedDay = isSameDay(date, selectedDate);
                                const isTodayDate = isToday(date);

                                return (
                                    <button
                                        key={i}
                                        onClick={() => setSelectedDate(date)}
                                        className={`p-3 text-center border-r border-[#EEF3F6] dark:border-dark-border last:border-r-0 transition ${isSelectedDay ? 'bg-[#0D5F6B] dark:bg-dark-teal' : isTodayDate ? 'bg-[#D9EEF2] dark:bg-dark-elevated' : 'hover:bg-[#F5F8FA] dark:hover:bg-dark-elevated'
                                            }`}
                                    >
                                        <p className={`text-xs font-medium ${isSelectedDay ? 'text-white/80' : 'text-[#7B8C9B] dark:text-dark-text-muted'}`}>
                                            {formatShortDay(date)}
                                        </p>
                                        <p className={`text-lg font-semibold ${isSelectedDay ? 'text-white' : isTodayDate ? 'text-[#0D5F6B] dark:text-dark-teal-light' : 'text-[#1A2832] dark:text-white'
                                            }`}>
                                            {formatDayNumber(date)}
                                        </p>
                                        {(dayTasks.length > 0 || events.filter(e => e.date === date.toISOString().split('T')[0]).length > 0) && (
                                            <div className={`mt-1 text-xs font-medium ${isSelectedDay ? 'text-white/80' : 'text-[#0D5F6B] dark:text-dark-teal-light'}`}>
                                                {dayTasks.length + events.filter(e => e.date === date.toISOString().split('T')[0]).length} items
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Selected Day Timeline */}
                        <div className="p-4 border-t border-[#EEF3F6]">
                            <h3 className="text-sm font-semibold text-[#7B8C9B] mb-3">{formatDate(selectedDate)}</h3>
                            <div className="max-h-[300px] overflow-y-auto space-y-2">
                                {(() => {
                                    // Combine tasks and events
                                    const tasks = getScheduledTasks(selectedDate).map(t => ({
                                        id: t.id,
                                        type: 'task',
                                        title: t.title,
                                        timeStart: t.timeStart || '',
                                        timeEnd: t.timeEnd || '',
                                        color: t.contextColor,
                                        bgColor: t.contextBgColor,
                                        original: t
                                    }));

                                    const calendarEvents = selectedDateEvents.map(e => ({
                                        id: e.id,
                                        type: 'event',
                                        title: e.title,
                                        timeStart: e.timeStart,
                                        timeEnd: e.timeEnd,
                                        color: 'white',
                                        bgColor: e.color,
                                        original: e
                                    }));

                                    const allItems = [...tasks, ...calendarEvents].sort((a, b) => {
                                        const timeA = parseTimeToHour(a.timeStart);
                                        const timeB = parseTimeToHour(b.timeStart);
                                        return timeA - timeB;
                                    });

                                    if (allItems.length === 0) {
                                        return <p className="text-sm text-[#7B8C9B] text-center py-8">No scheduled items</p>;
                                    }

                                    return allItems.map(item => (
                                        <div
                                            key={item.id}
                                            className="rounded-lg p-3 text-sm flex justify-between items-start group"
                                            style={item.type === 'task' ? {
                                                backgroundColor: item.bgColor,
                                                borderLeft: `3px solid ${item.color}`,
                                            } : {
                                                backgroundColor: item.bgColor,
                                                color: 'white'
                                            }}
                                            onClick={() => item.type === 'event'
                                                ? handleEventClick(item.original as CalendarEvent)
                                                : handleTaskClick(item.original as Task)
                                            }
                                        >
                                            <div className="flex-1 cursor-pointer">
                                                <p className={`font-medium ${item.type === 'event' ? 'text-white' : 'text-[#1A2832]'}`}>
                                                    {item.title}
                                                </p>
                                                <p className={`text-xs ${item.type === 'event' ? 'text-white/80' : 'text-[#7B8C9B]'}`}>
                                                    {item.timeStart} - {item.timeEnd}
                                                </p>
                                            </div>
                                            {item.type === 'event' && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteEvent(item.id);
                                                    }}
                                                    className="w-6 h-6 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 hover:bg-white/20 transition"
                                                >
                                                    <Trash2 className="w-3 h-3 text-white" />
                                                </button>
                                            )}
                                        </div>
                                    ));
                                })()}
                            </div>
                        </div>

                        {/* Unscheduled Tasks for Selected Day */}
                        {getUnscheduledTasks(selectedDate).length > 0 && (
                            <div className="p-4 border-t border-[#EEF3F6]">
                                <h3 className="text-sm font-semibold text-[#7B8C9B] mb-3">Unscheduled</h3>
                                <div className="space-y-2">
                                    {getUnscheduledTasks(selectedDate).map(task => (
                                        <div
                                            key={task.id}
                                            className="flex items-center gap-3 bg-[#F5F8FA] rounded-xl p-3"
                                        >
                                            <div className={`w-3 h-3 rounded-full ${task.completed ? 'bg-[#3DA58A]' : 'bg-[#DCE4EA]'}`} />
                                            <p className="text-sm font-medium text-[#1A2832]">{task.title}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* MONTH VIEW */}
            {viewMode === 'month' && (
                <section className="mb-4 px-4">
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-2">
                            <button onClick={goToPrevMonth} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F8FA]">
                                <ChevronLeft className="w-5 h-5 text-[#7B8C9B]" />
                            </button>
                            <h2 className="text-xl font-semibold text-[#1A2832]">{formatMonthYear(selectedDate)}</h2>
                            <button onClick={goToNextMonth} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F8FA]">
                                <ChevronRight className="w-5 h-5 text-[#7B8C9B]" />
                            </button>
                        </div>
                        <button
                            onClick={() => setShowAddEvent(true)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#0D5F6B] text-white text-sm font-semibold"
                        >
                            <Plus className="w-4 h-4" />
                            Add
                        </button>
                    </div>

                    {/* Month Grid */}
                    <div className="bg-white dark:bg-dark-card rounded-2xl shadow-[0_4px_16px_rgba(13,95,107,0.08)] dark:shadow-dark overflow-hidden">
                        {/* Day Labels */}
                        <div className="grid grid-cols-7 border-b border-[#EEF3F6] dark:border-dark-border">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                <div key={day} className="p-2 text-center border-r border-[#EEF3F6] dark:border-dark-border last:border-r-0">
                                    <span className="text-xs font-medium text-[#7B8C9B] dark:text-dark-text-muted">{day}</span>
                                </div>
                            ))}
                        </div>

                        {/* Calendar Grid */}
                        <div className="grid grid-cols-7">
                            {calendarDays.map(({ date, isCurrentMonth }, i) => {
                                const dayTasks = getTasksForDate(date);
                                const isSelectedDay = isSameDay(date, selectedDate);
                                const isTodayDate = isToday(date);

                                return (
                                    <button
                                        key={i}
                                        onClick={() => {
                                            setSelectedDate(date);
                                            setViewMode('day');
                                        }}
                                        className={`min-h-[80px] p-2 border-r border-b border-[#EEF3F6] dark:border-dark-border last:border-r-0 text-left transition ${isSelectedDay
                                            ? 'bg-[#0D5F6B] dark:bg-dark-teal'
                                            : isTodayDate
                                                ? 'bg-[#D9EEF2] dark:bg-dark-elevated'
                                                : isCurrentMonth
                                                    ? 'hover:bg-[#F5F8FA] dark:hover:bg-dark-elevated'
                                                    : 'bg-[#FAFBFC] dark:bg-dark-bg'
                                            }`}
                                    >
                                        <p className={`text-sm font-semibold mb-1 ${isSelectedDay
                                            ? 'text-white'
                                            : isTodayDate
                                                ? 'text-[#0D5F6B] dark:text-dark-teal-light'
                                                : isCurrentMonth
                                                    ? 'text-[#1A2832] dark:text-white'
                                                    : 'text-[#A8B5C1] dark:text-dark-text-muted'
                                            }`}>
                                            {date.getDate()}
                                        </p>
                                        {(dayTasks.length > 0 || events.filter(e => e.date === date.toISOString().split('T')[0]).length > 0) && (
                                            <div className="space-y-0.5">
                                                {/* Combine tasks and events for preview */}
                                                {[...dayTasks.map(t => ({ title: t.title, color: t.contextColor || '#0D5F6B', bg: t.contextBgColor || '#E0F2F1' })),
                                                ...events.filter(e => e.date === date.toISOString().split('T')[0]).map(e => ({ title: e.title, color: 'white', bg: e.color }))]
                                                    .slice(0, 2).map((item, idx) => (
                                                        <div
                                                            key={idx}
                                                            className={`text-[10px] truncate px-1 py-0.5 rounded ${isSelectedDay ? 'bg-white/20 text-white' : ''
                                                                }`}
                                                            style={isSelectedDay ? {} : {
                                                                backgroundColor: item.bg,
                                                                color: item.color
                                                            }}
                                                        >
                                                            {item.title}
                                                        </div>
                                                    ))}
                                                {(dayTasks.length + events.filter(e => e.date === date.toISOString().split('T')[0]).length) > 2 && (
                                                    <p className={`text-[10px] ${isSelectedDay ? 'text-white/70' : 'text-[#7B8C9B]'}`}>
                                                        +{dayTasks.length + events.filter(e => e.date === date.toISOString().split('T')[0]).length - 2} more
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* Quick Actions */}
            <section className="px-4 pb-4">
                <div className="flex gap-3">
                    <button
                        onClick={() => setShowFindTime(true)}
                        className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-white border border-[#DCE4EA] shadow-[0_2px_8px_rgba(13,95,107,0.06)] text-[#0D5F6B] hover:bg-[#F5F8FA] transition"
                    >
                        <Search className="w-5 h-5" />
                        <span className="text-sm font-semibold">Find Time</span>
                    </button>
                </div>
            </section>

            {/* Item Detail Modal */}
            {selectedItem && (
                <ItemDetailModal
                    item={selectedItem}
                    onClose={closeItemDetail}
                    updateTask={updateTask}
                    updateEvent={updateEvent}
                />
            )}

            {/* Add Event Modal */}
            {showAddEvent && (
                <AddEventModal onClose={() => setShowAddEvent(false)} selectedDate={selectedDate} />
            )}

            {/* Find Time Modal */}
            {showFindTime && (
                <FindTimeModal
                    selectedDate={selectedDate}
                    scheduledTasks={scheduledTasks}
                    events={events}
                    unscheduledTasks={unscheduledTasks}
                    onScheduleTask={(taskId, hour) => {
                        const startTime = hourTo12Format(hour);
                        const endTime = hourTo12Format(hour + 1);
                        const dateStr = selectedDate.toISOString().split('T')[0];
                        updateTask(taskId, {
                            timeStart: startTime,
                            timeEnd: endTime,
                            dueDate: dateStr,
                        });
                    }}
                    onClose={() => setShowFindTime(false)}
                    parseTimeToHour={parseTimeToHour}
                />
            )}
        </div>
    );
}



// Add Event Modal Component
interface AddEventModalProps {
    onClose: () => void;
    selectedDate: Date;
}

function AddEventModal({ onClose, selectedDate }: AddEventModalProps) {
    const { addEvent } = useAppState();
    const [title, setTitle] = useState('');
    const [timeStart, setTimeStart] = useState('09:00');
    const [timeEnd, setTimeEnd] = useState('10:00');
    const [location, setLocation] = useState('');
    const [eventType, setEventType] = useState<CalendarEvent['type']>('meeting');
    const [recurrencePattern, setRecurrencePattern] = useState<'none' | 'daily' | 'weekly' | 'monthly' | 'weekdays'>('none');

    const eventColors: Record<CalendarEvent['type'], string> = {
        meeting: '#8B9FD8',
        task: '#2A7A85',
        focus: '#3DA58A',
        personal: '#7B61C4',
    };

    const recurrenceOptions = [
        { value: 'none', label: 'Does not repeat' },
        { value: 'daily', label: 'Daily' },
        { value: 'weekdays', label: 'Every weekday (Mon-Fri)' },
        { value: 'weekly', label: 'Weekly' },
        { value: 'monthly', label: 'Monthly' },
    ] as const;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim()) return;

        addEvent({
            title: title.trim(),
            date: selectedDate.toISOString().split('T')[0],
            timeStart: formatTime(timeStart),
            timeEnd: formatTime(timeEnd),
            type: eventType,
            color: eventColors[eventType],
            location: location.trim() || undefined,
            recurrence: recurrencePattern !== 'none' ? {
                pattern: recurrencePattern,
                interval: 1,
            } : undefined,
        });

        onClose();
    };

    const formatTime = (time: string) => {
        const [hours, minutes] = time.split(':');
        const h = parseInt(hours);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        return `${h12}:${minutes} ${ampm}`;
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
            <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-6 pb-8">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-[#1A2832]">Add Event</h2>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F8FA]"
                    >
                        <X className="w-5 h-5 text-[#7B8C9B]" />
                    </button>
                </div>

                <p className="text-sm text-[#7B8C9B] mb-4">
                    {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] mb-2">Title</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Event title"
                            className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] focus:outline-none focus:border-[#0D5F6B] text-[#1A2832]"
                            autoFocus
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] mb-2">Type</label>
                        <div className="flex gap-2">
                            {(['meeting', 'focus', 'personal'] as const).map((type) => (
                                <button
                                    key={type}
                                    type="button"
                                    onClick={() => setEventType(type)}
                                    className={`flex-1 py-2 rounded-lg text-sm font-semibold capitalize transition ${eventType === type ? 'text-white' : 'bg-[#F5F8FA] text-[#4A5C6A]'
                                        }`}
                                    style={eventType === type ? { backgroundColor: eventColors[type] } : {}}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-[#1A2832] mb-2">Start</label>
                            <input
                                type="time"
                                value={timeStart}
                                onChange={(e) => setTimeStart(e.target.value)}
                                className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] focus:outline-none focus:border-[#0D5F6B] text-[#1A2832]"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-[#1A2832] mb-2">End</label>
                            <input
                                type="time"
                                value={timeEnd}
                                onChange={(e) => setTimeEnd(e.target.value)}
                                className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] focus:outline-none focus:border-[#0D5F6B] text-[#1A2832]"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] mb-2">Location (optional)</label>
                        <input
                            type="text"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            placeholder="Zoom, Office, etc."
                            className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] focus:outline-none focus:border-[#0D5F6B] text-[#1A2832]"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] mb-2">Repeat</label>
                        <select
                            value={recurrencePattern}
                            onChange={(e) => setRecurrencePattern(e.target.value as typeof recurrencePattern)}
                            className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] focus:outline-none focus:border-[#0D5F6B] text-[#1A2832] bg-white"
                        >
                            {recurrenceOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <button
                        type="submit"
                        disabled={!title.trim()}
                        className="w-full py-4 rounded-xl bg-[#0D5F6B] text-white text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Add Event
                    </button>
                </form>
            </div>
        </div>
    );
}

// Find Time Modal Component
interface FindTimeModalProps {
    selectedDate: Date;
    scheduledTasks: Task[];
    events: CalendarEvent[];
    unscheduledTasks: Task[];
    onScheduleTask: (taskId: string, hour: number) => void;
    onClose: () => void;
    parseTimeToHour: (time: string) => number;
}

function FindTimeModal({
    selectedDate,
    scheduledTasks,
    events,
    unscheduledTasks,
    onScheduleTask,
    onClose,
    parseTimeToHour,
}: FindTimeModalProps) {
    const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

    // Generate time slots and find which are busy
    const timeSlots = useMemo(() => {
        const slots: { hour: number; label: string; isBusy: boolean; busyWith?: string }[] = [];

        for (let hour = 6; hour <= 21; hour++) { // 6 AM to 9 PM
            const label = hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`;

            // Check if any task is scheduled at this hour
            const taskAtHour = scheduledTasks.find(t => {
                if (!t.timeStart) return false;
                const taskHour = parseTimeToHour(t.timeStart);
                return taskHour === hour;
            });

            // Check if any event is scheduled at this hour
            const eventAtHour = events.find(e => {
                const eventHour = parseTimeToHour(e.timeStart);
                return eventHour === hour;
            });

            slots.push({
                hour,
                label,
                isBusy: !!(taskAtHour || eventAtHour),
                busyWith: taskAtHour?.title || eventAtHour?.title,
            });
        }

        return slots;
    }, [scheduledTasks, events, parseTimeToHour]);

    const availableSlots = timeSlots.filter(s => !s.isBusy);

    const handleSchedule = (hour: number) => {
        if (selectedTaskId) {
            onScheduleTask(selectedTaskId, hour);
            setSelectedTaskId(null);
            onClose();
        }
    };

    const formatDate = (date: Date) => {
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
        });
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
            <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col">
                {/* Header */}
                <div className="p-4 border-b border-[#EEF3F6] flex-shrink-0">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <CalendarCheck className="w-5 h-5 text-[#0D5F6B]" />
                            <h2 className="text-xl font-semibold text-[#1A2832]">Find Available Time</h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F8FA]"
                        >
                            <X className="w-5 h-5 text-[#7B8C9B]" />
                        </button>
                    </div>
                    <p className="text-sm text-[#7B8C9B] mt-1">{formatDate(selectedDate)}</p>
                </div>

                {/* Task Selection */}
                {unscheduledTasks.length > 0 && (
                    <div className="p-4 border-b border-[#EEF3F6] flex-shrink-0">
                        <h3 className="text-sm font-semibold text-[#7B8C9B] mb-2">Select a task to schedule</h3>
                        <div className="flex flex-wrap gap-2">
                            {unscheduledTasks.map(task => (
                                <button
                                    key={task.id}
                                    onClick={() => setSelectedTaskId(selectedTaskId === task.id ? null : task.id)}
                                    className={`px-3 py-2 rounded-lg text-sm font-medium transition ${selectedTaskId === task.id
                                        ? 'bg-[#0D5F6B] text-white'
                                        : 'bg-[#F5F8FA] text-[#1A2832] hover:bg-[#EEF3F6]'
                                        }`}
                                >
                                    {task.title}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Time Slots */}
                <div className="flex-1 overflow-y-auto p-4">
                    <h3 className="text-sm font-semibold text-[#7B8C9B] mb-3">
                        {availableSlots.length} available slot{availableSlots.length !== 1 ? 's' : ''}
                    </h3>

                    <div className="space-y-2">
                        {timeSlots.map(slot => (
                            <div
                                key={slot.hour}
                                className={`flex items-center justify-between p-3 rounded-xl transition ${slot.isBusy
                                    ? 'bg-[#FEF3F3] border border-[#FECACA]'
                                    : 'bg-[#F5F8FA] hover:bg-[#EEF3F6]'
                                    }`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 text-sm font-semibold ${slot.isBusy ? 'text-[#D66B5C]' : 'text-[#0D5F6B]'}`}>
                                        {slot.label}
                                    </div>
                                    {slot.isBusy ? (
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-[#D66B5C]" />
                                            <span className="text-sm text-[#D66B5C]">{slot.busyWith}</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-[#3DA58A]" />
                                            <span className="text-sm text-[#3DA58A]">Available</span>
                                        </div>
                                    )}
                                </div>

                                {!slot.isBusy && selectedTaskId && (
                                    <button
                                        onClick={() => handleSchedule(slot.hour)}
                                        className="px-3 py-1.5 rounded-lg bg-[#0D5F6B] text-white text-sm font-semibold"
                                    >
                                        Schedule
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                {!selectedTaskId && unscheduledTasks.length > 0 && (
                    <div className="p-4 border-t border-[#EEF3F6] flex-shrink-0">
                        <p className="text-sm text-[#7B8C9B] text-center">
                            Select a task above to schedule it
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

{/* Item Detail Modal Component */ }
interface ItemDetailModalProps {
    item: { type: 'event'; data: CalendarEvent } | { type: 'task'; data: Task };
    onClose: () => void;
    updateTask: (id: string, updates: Partial<Task>) => void;
    updateEvent: (id: string, updates: Partial<CalendarEvent>) => void;
}

function ItemDetailModal({ item, onClose, updateTask, updateEvent }: ItemDetailModalProps) {
    const [isRescheduling, setIsRescheduling] = useState(false);
    const [date, setDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');

    // Initialize state when rescheduling starts
    useEffect(() => {
        if (isRescheduling) {
            const d = item.type === 'event' ? item.data.date : item.data.dueDate;
            const s = item.data.timeStart || '09:00 AM';
            const e = item.data.timeEnd || '10:00 AM';

            // Ensure date is YYYY-MM-DD
            const dateStr = d || new Date().toISOString().split('T')[0];

            setDate(dateStr);
            setStartTime(s);
            setEndTime(e);
        }
    }, [isRescheduling, item]);

    const handleSave = () => {
        if (item.type === 'task') {
            updateTask(item.data.id, {
                dueDate: date,
                timeStart: startTime,
                timeEnd: endTime
            });
        } else {
            updateEvent(item.data.id, {
                date: date,
                timeStart: startTime,
                timeEnd: endTime
            });
        }
        setIsRescheduling(false);
        onClose();
    };

    const title = item.data.title;
    const timeRange = `${item.data.timeStart || 'No time'} - ${item.data.timeEnd || 'No time'}`;
    const location = item.type === 'event' ? item.data.location : undefined;
    const attendees = item.type === 'event' ? item.data.attendees : undefined;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
            <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl p-6 shadow-xl animate-in slide-in-from-bottom duration-200">
                {!isRescheduling ? (
                    <>
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h2 className="text-xl font-semibold text-[#1A2832]">{title}</h2>
                                <div className="flex items-center gap-2 mt-1 text-[#4A5C6A]">
                                    <Clock className="w-4 h-4" />
                                    <span className="text-sm">{timeRange}</span>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F8FA]"
                            >
                                <X className="w-5 h-5 text-[#7B8C9B]" />
                            </button>
                        </div>

                        {location && (
                            <div className="flex items-center gap-2 mb-3 text-[#4A5C6A]">
                                <MapPin className="w-4 h-4" />
                                <span className="text-sm">{location}</span>
                            </div>
                        )}

                        {attendees && (
                            <div className="flex items-center gap-2 mb-4 text-[#4A5C6A]">
                                <Users className="w-4 h-4" />
                                <span className="text-sm">{attendees} attendees</span>
                            </div>
                        )}

                        {item.type === 'task' && (
                            <div className="flex items-center gap-2 mb-4">
                                <span className="px-2 py-1 rounded text-xs font-semibold"
                                    style={{ backgroundColor: item.data.contextBgColor, color: item.data.contextColor }}>
                                    {item.data.context}
                                </span>
                            </div>
                        )}

                        <div className="flex gap-3 mt-6">
                            {item.type === 'event' && (
                                <button className="flex-1 py-3 rounded-xl bg-[#0D5F6B] text-white font-semibold hover:bg-[#0A4A53] transition">
                                    Join Meeting
                                </button>
                            )}
                            <button
                                onClick={() => setIsRescheduling(true)}
                                className={`flex-1 py-3 rounded-xl border-2 font-semibold transition ${item.type === 'task' ? 'bg-[#0D5F6B] text-white border-[#0D5F6B]' : 'border-[#0D5F6B] text-[#0D5F6B] hover:bg-[#F0F7F8]'
                                    }`}
                            >
                                Reschedule
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-semibold text-[#1A2832]">Reschedule</h2>
                            <button
                                onClick={() => setIsRescheduling(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F8FA]"
                            >
                                <X className="w-5 h-5 text-[#7B8C9B]" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-[#4A5C6A] mb-1">Date</label>
                                <input
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    className="w-full p-3 rounded-xl border border-[#EEF3F6] focus:border-[#0D5F6B] focus:ring-0 outline-none transition"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-[#4A5C6A] mb-1">Start Time</label>
                                    <input
                                        type="text"
                                        value={startTime}
                                        onChange={(e) => setStartTime(e.target.value)}
                                        placeholder="09:00 AM"
                                        className="w-full p-3 rounded-xl border border-[#EEF3F6] focus:border-[#0D5F6B] focus:ring-0 outline-none transition"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[#4A5C6A] mb-1">End Time</label>
                                    <input
                                        type="text"
                                        value={endTime}
                                        onChange={(e) => setEndTime(e.target.value)}
                                        placeholder="10:00 AM"
                                        className="w-full p-3 rounded-xl border border-[#EEF3F6] focus:border-[#0D5F6B] focus:ring-0 outline-none transition"
                                    />
                                </div>
                            </div>

                            <button
                                onClick={handleSave}
                                className="w-full py-3 mt-4 rounded-xl bg-[#0D5F6B] text-white font-semibold hover:bg-[#0A4A53] transition"
                            >
                                Save Changes
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
